import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'

import { useGetMaterialsQuery } from '@/app/services/materials'
import { selectOrderItems, useUpdateOrderItemsMutation } from '@/app/services/orderItems'
import { selectOrder, useUpdateOrderMutation } from '@/app/services/orders'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { getSortFunction } from '@/common/utils/OrderUtils/OrderUtilities'

const OrderItemListSort = () => {
    const { t } = useTranslation()
    const { showMutationError } = useMutationErrorSnackbar()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const selectedOrder = useSelector((state) => selectOrder(state, { organisationId: organisationId, orderId }))
    const orderItems = useSelector((state) => selectOrderItems(state, { organisationId: organisationId, orderId }))

    const { data: materials } = useGetMaterialsQuery({ organisationId })

    const [selectedCriteria, setSelectedCriteria] = useState(selectedOrder.sortOrderItemsBy ?? 'Custom')
    const [selectedDirection, setSelectedDirection] = useState(selectedOrder.sortOrderItemsAsc ? 'ASC' : 'DESC')

    const [updateOrder] = useUpdateOrderMutation()
    const [updateOrderItems] = useUpdateOrderItemsMutation()

    const handleUpdateOrder = async (updatedFields) => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    ...selectedOrder,
                    ...updatedFields,
                },
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('$t(An error occurred) updating the order.'))
        }
    }

    const handleUpdateOrderItems = async (sortedList) => {
        try {
            await updateOrderItems({
                organisationId,
                orderId,
                orderItems: sortedList,
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('Failed to reorder order items. Undoing changes'))
        }
    }

    const handleCriteriaChange = (event) => {
        const { value } = event.target
        setSelectedCriteria(value)

        const sortedList = [...orderItems]
            .sort(getSortFunction(value, selectedDirection, materials))
            .map((orderItem, index) => ({ ...orderItem, index }))

        handleUpdateOrder({ sortOrderItemsBy: value })
        handleUpdateOrderItems(sortedList)
    }

    const handleDirectionChange = (event) => {
        const { value } = event.target
        setSelectedDirection(value)

        const sortedList = [...orderItems]
            .sort(getSortFunction(selectedCriteria, value, materials))
            .map((orderItem, index) => ({ ...orderItem, index }))

        handleUpdateOrder({ sortOrderItemsAsc: value == 'ASC' })
        handleUpdateOrderItems(sortedList)
    }

    useEffect(() => {
        setSelectedCriteria(selectedOrder.sortOrderItemsBy ?? 'Custom')
        setSelectedDirection(selectedOrder.sortOrderItemsAsc ? 'ASC' : 'DESC')
    }, [selectedOrder])

    return (
        <>
            <FormControl variant="outlined">
                <InputLabel
                    data-testid="sort-by-label"
                    id="selected-criteria-label"
                >
                    {t('Sort by')}
                </InputLabel>
                <Select
                    data-testid="sort-by"
                    id="selected-criteria"
                    label={t('Sort by')}
                    labelId="selected-criteria-label"
                    size="small"
                    value={selectedCriteria}
                    onChange={handleCriteriaChange}
                >
                    <MenuItem value={'Default'}>{t('Upload order')}</MenuItem>
                    {selectedCriteria === 'Custom' ? <MenuItem value={'Custom'}>{t('User defined')}</MenuItem> : null}
                    <MenuItem value={'Name'}>{t('Part name')}</MenuItem>
                    <MenuItem value={'MaterialThickness'}>{t('Material & Thickness')}</MenuItem>
                </Select>
            </FormControl>
            <FormControl variant="outlined">
                <InputLabel
                    data-testid="order-by-label"
                    id="selected-direction-label"
                >
                    {t('Order by')}
                </InputLabel>
                <Select
                    data-testid="order-by"
                    disabled={selectedCriteria === 'Custom'}
                    id="selected-direction"
                    label={t('Order by')}
                    labelId="selected-direction-label"
                    size="small"
                    value={selectedDirection}
                    onChange={handleDirectionChange}
                >
                    <MenuItem value={'ASC'}>{t('ASC')}</MenuItem>
                    <MenuItem value={'DESC'}>{t('DESC')}</MenuItem>
                </Select>
            </FormControl>
        </>
    )
}

export default OrderItemListSort
